'use client';

import { useInterval, useNoHydrationError } from '@willbooster/shared-lib-react';
import { createContext, useContext, useState } from 'react';

import { type Dayjs, dayjs } from '@/utils/dayjs';

const RenderTimeContext = createContext<number | undefined>(undefined);

/** Provides the time at which the server rendered the page, which `useNow` returns while hydrating. */
export const RenderTimeProvider: React.FC<{ children: React.ReactNode; renderedAt: number }> = ({
  children,
  renderedAt,
}) => <RenderTimeContext value={renderedAt}>{children}</RenderTimeContext>;

/**
 * The current time, refreshed every `refreshIntervalMs` if given. While hydrating, it is the server's render time, so
 * that time-dependent output matches the server-rendered HTML even when the browser's clock differs from the server's.
 */
export function useNow(refreshIntervalMs?: number): Dayjs {
  const renderedAt = useContext(RenderTimeContext);
  const [clientNow, setClientNow] = useState(Date.now);
  useInterval(() => {
    setClientNow(Date.now());
  }, refreshIntervalMs);
  return dayjs(useNoHydrationError(clientNow, renderedAt ?? clientNow));
}
